import React, { useEffect, useState } from 'react';
import { Col, Container, Dropdown, Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import ActionMenu from '../../components/ActionMenu/ActionMenu';
import ActionMenuItem from '../../components/ActionMenu/ActionMenuItem';
import Authorize from '../../components/AuthComponent/Authorize';
import Button from '../../components/Buttons/Button';
import SaveButton from '../../components/Buttons/SaveButton';
import ButtonBar from '../../components/Fields/ButtonBar';
import DateControl from '../../components/Fields/DateControl';
import Field from '../../components/Fields/Field';
import FieldLabel from '../../components/Fields/FieldLabel';
import Form from '../../components/Fields/Form';
import SelectListField from '../../components/Fields/SelectListField';
import TextareaControl from '../../components/Fields/TextareaControl';
import TextareaField from '../../components/Fields/TextareaField';
import TextboxField from '../../components/Fields/TextboxField';
import { FieldConfig, FormConfig } from '../../components/Fields/Types';
import Title from '../../components/Headers/Title';
import useSelectListOptions from '../../components/Hooks/SelectListOptionsHook/useSelectListOptions';
import useValidation from '../../components/Hooks/ValidationHook/useValidation';
import SpinnerOverlay from '../../components/Spinner/SpinnerOverlay';
import { HorizontalTabs } from '../../components/Tabs/HorizontalTabs';
import { ApplicationFunctions, useAuth } from '../../contexts/authContext';
import { useNotifications } from '../../contexts/notificationContext';
import BusinessPartnerModel from '../../models/BusinessPartnerModel';
import CommentModel from '../../models/CommentModel';
import DataService from '../../services/data.service';
import IdParams from '../../types/IdParams';
import { DataResult, Result } from '../../types/Result';
import Unauthorized from '../Authentication/Unauthorized';
import CommentView from '../Items/Components/Comment';
import EditBusinessPartnerReferencesPopup from './Popups/BusinessPartner_popup_EditReferenceCodes';
import BusinessPartner_popup_Export from './Popups/BusinessPartner_popup_Export';
import BusinessPartner_tab_Addresses from './Tabs/BusinessPartner_tab_Addresses';
import BusinessPartner_tab_Attachments from './Tabs/BusinessPartner_tab_Attachments';
import BusinessPartner_tab_BusinessPartnerGroups from './Tabs/BusinessPartner_tab_BusinessPartnerGroups';
import BusinessPartner_tab_Contactpersons from './Tabs/BusinessPartner_tab_Contactpersons';
import BusinessPartner_tab_Employees from './Tabs/BusinessPartner_tab_Employees';
import BusinessPartner_tab_Items from './Tabs/BusinessPartner_tab_Items';
import BusinessPartner_tab_Logins from './Tabs/BusinessPartner_tab_Logins';
import BusinessPartner_tab_Projects from './Tabs/BusinessPartner_tab_Projects';

const BusinessPartnerDetailsView: React.FC = () => {
    const businessPartnerTypes = useSelectListOptions('api/v1/selectlist/BusinessPartnerTypes?active=true&applicationFunction=' + ApplicationFunctions.UpdateBusinessPartner, '-- Select type');
    const [serverErrors, setServerErrors] = useState<DataResult<BusinessPartnerModel>>({ isSuccesful: true, messages: [], data: undefined });
    const { handleResult, handleException } = useNotifications();
    const [formData, setFormData] = useState<BusinessPartnerModel>();
    const [isAuthorized, setIsAuthorized] = useState<boolean>(true);

    const [showSetImagePopup, setShowSetImagePopup] = useState<boolean>(false);
    const [showChangeReferenceCodesPopup, setShowChangeReferenceCodesPopup] = useState<boolean>(false);
    const [showExportPopup, setShowExportPopup] = useState<boolean>(false);
    const [showGenerateDocumentPopup, setShowGenerateDocumentPopup] = useState<boolean>(false);
    const [showSpinner, setShowSpinner] = useState(true);
    const [comments, setComments] = useState<string>('')
    const { user } = useAuth()

    let businessPartnerId: number | undefined;
    let idParams: IdParams = useParams();

    if (idParams.id && !Number.isNaN(parseInt(idParams.id))) {
        businessPartnerId = parseInt(idParams.id);
    }

    useEffect(() => {
        if (businessPartnerId !== undefined) {
            setShowSpinner(true);
            DataService.getById<BusinessPartnerModel>('api/v1/BusinessPartners/', businessPartnerId)
                .then(response => {
                    setShowSpinner(false);
                    setFormData(response);
                })
                .catch(e => {
                    setShowSpinner(false);
                    if (e.response?.data?.status === 401)
                        setIsAuthorized(false);
                    else
                        handleException(e);
                });
        }
    }, [businessPartnerId]);

    function loadComments() {
        if (formData && businessPartnerId !== undefined) {
            setShowSpinner(true);
            DataService.get<CommentModel[]>('api/v1/comments/ByBusinessPartner/' + businessPartnerId)
                .then(response => {
                    setShowSpinner(false);
                    formData.comments = response
                    setFormData(x => ({ ...formData }));
                })
                .catch(e => { setShowSpinner(false); handleException(e); });
        }
    }


    function submit() {
        if (formData) {
            DataService
                .update<BusinessPartnerModel>(`api/v1/BusinessPartners/`, formData)
                .then(response => {
                    handleResult(response);
                    setServerErrors(response);
                    if (response.isSuccesful === true && response.data)
                        setFormData(response.data);
                })
                .catch(e => handleException(e));
        }
    }

    function submitComments() {
        setShowSpinner(true);
        if (formData && comments != '') {
            const data = {
                discussionId: formData.discussionId,
                businessPartnerId: formData.id,
                text: comments,
            };
            DataService
                .create<Result>(`api/v1/comments/`, data)
                .then(response => {
                    setShowSpinner(false);
                    handleResult(response);
                    if (response.isSuccesful) {
                        setComments('')
                        loadComments()
                    }
                })
                .catch(e => {
                    setShowSpinner(false);
                    handleException(e);
                });
        }
    }

    let fields: FieldConfig[] = [
        { name: 'businessPartnerTypeId', label: 'Type', labelSize: 3 },
        { name: 'name', label: 'Name', labelSize: 3, validationRules: [{ type: 'isRequired' }] },
        { name: 'division', label: 'Division', labelSize: 3 },
        { name: 'notes', label: 'Notes', labelSize: 3 },
        { name: 'beginDate', label: 'Active from', labelSize: 3, validationRules: [{ type: 'isRequired' }, { type: 'isDate' }] },
        { name: 'endDate', label: 'Active untill', labelSize: 'auto', validationRules: [ { type: 'isDate' }] },
    ];

    const formConfig: FormConfig = {
        fields: fields,
        handleSubmit: submit,
        showErrors: 'always',
    };

    const { getLabelProps, getControlProps, getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData, serverErrors);

    if (!isAuthorized)
        return (<Unauthorized />);

    if (!formData)
        return <SpinnerOverlay />

    const formActions =
        <ActionMenu title="Actions">
            <ActionMenuItem width={260} icon="fa fa-image" title="Change logo" appFunction={ApplicationFunctions.UpdateBusinessPartner} onClick={() => { setShowSetImagePopup(true); }} disabled />
            <ActionMenuItem icon="fa fa-edit" title="Change reference codes" appFunction={ApplicationFunctions.UpdateBusinessPartner} onClick={() => { setShowChangeReferenceCodesPopup(true); }} />
            <ActionMenuItem icon="fa fa-magic" title="Generate a document" appFunction={ApplicationFunctions.UpdateBusinessPartner} onClick={() => { setShowGenerateDocumentPopup(true); }} disabled />
            <Dropdown.Divider />
            <ActionMenuItem icon="fa fa-plus" iconVariant="success" title="Add contact info" appFunction={ApplicationFunctions.UpdateBusinessPartner} disabled />
            <ActionMenuItem icon="fa fa-sign-out" title="Export business-partner" appFunction={ApplicationFunctions.UpdateBusinessPartner} onClick={() => { setShowExportPopup(true); }} />
            <Dropdown.Divider />
            <ActionMenuItem icon="fa fa-trash" iconVariant="danger" title="Delete business-partner" appFunction={ApplicationFunctions.DeleteBusinessPartner} disabled />
        </ActionMenu>

    return (
        <div className='main-content'>
            <Form title='Business-partner' actions={formActions} footer={(<>
                    <ButtonBar offset={3}>
                        <Authorize applicationFunction={ApplicationFunctions.UpdateBusinessPartner}>
                            <SaveButton  {...getSubmitButtonProps()} />
                        </Authorize>
                    </ButtonBar>
                </>)}>
                <Container fluid>
                    <Row>
                        <Col md='10'>
                            <SelectListField {...getFieldProps('businessPartnerTypeId')} options={businessPartnerTypes} />
                            <TextboxField {...getFieldProps('name')} />
                            <TextboxField {...getFieldProps('division')} />
                            <Field {...getLabelProps('beginDate')}>
                                <DateControl {...getControlProps('beginDate')} />
                                <span style={{ padding: '7px 15px' }}><FieldLabel {...getLabelProps('endDate')} /></span>
                                <DateControl {...getControlProps('endDate')} />
                            </Field>
                            <TextareaField {...getFieldProps('notes')} rows={10} cols={50} />
                        </Col>
                        <Col md='2'>
                            <img src={formData.thumbnailUrl} style={{ width: '100%' }} />
                            <Title text='Comments' size={4} />
                            <div>
                                <div style={{ border: '1px solid #e3e3e3', borderRadius: 10, marginBottom: 3 }}>
                                    <div style={{ backgroundColor: '#e3e3e3', borderTopLeftRadius: 10, borderTopRightRadius: 10, height: 40, borderBottomColor: 'black' }} >
                                        <div className='user' style={{ padding: 5 }}><div className='thumbnail' style={{}}><img height='32' src={user?.thumbnailUrl} /></div></div>
                                        <div className='pull-left' style={{ paddingLeft: 5 }}>{user?.name}</div>
                                    </div>
                                    <div style={{ margin: 5 }}>
                                        <TextareaControl name='comments' value={comments} onChange={(value) => setComments(value)} validationResults={[]} />
                                    </div>
                                </div>
                                <div>
                                    <Button type='button' text='Submit' onClick={() => submitComments()}></Button>
                                </div>
                            </div>
                            <div style={{ marginTop: 12 }}>
                                {formData && formData.comments &&
                                    formData.comments.map((value, index) => {
                                        return <CommentView comment={value} key={index} />
                                    })
                                }
                            </div>

                        </Col>
                    </Row>
                </Container>
            </Form>
            {businessPartnerId && (
                <Container fluid>
                    <Row>
                        <Col md='12'>
                            <HorizontalTabs
                                id='main-tabs'
                                defaultActiveKey='tab_contactpersons'
                                tabs={[
                                    {
                                        title: 'Contactpersons',
                                        eventKey: 'tab_contactpersons',
                                        component: <BusinessPartner_tab_Contactpersons businessPartnerId={businessPartnerId} businessPartnerTypeId={formData.businessPartnerTypeId} />
                                    },
                                    {
                                        title: 'Addresses',
                                        eventKey: 'tab_addresses',
                                        component: <BusinessPartner_tab_Addresses businessPartnerId={businessPartnerId} businessPartnerTypeId={formData.businessPartnerTypeId} />
                                    },
                                    {
                                        title: 'Logins',
                                        eventKey: 'tab_logins',
                                        component: <BusinessPartner_tab_Logins businessPartnerId={businessPartnerId} businessPartnerTypeId={formData.businessPartnerTypeId} />
                                    },
                                    {
                                        title: 'Employees',
                                        eventKey: 'tab_employees',
                                        component: <BusinessPartner_tab_Employees businessPartnerId={businessPartnerId} businessPartnerTypeId={formData.businessPartnerTypeId} />
                                    },
                                    {
                                        title: 'Attachments',
                                        eventKey: 'tab_attachments',
                                        component: <BusinessPartner_tab_Attachments businessPartnerId={businessPartnerId} businessPartnerTypeId={formData.businessPartnerTypeId} />
                                    },
                                    {
                                        title: 'Projects',
                                        eventKey: 'tab_projects',
                                        component: <BusinessPartner_tab_Projects businessPartnerId={businessPartnerId} businessPartnerTypeId={formData.businessPartnerTypeId} />
                                    },
                                    {
                                        title: 'Items',
                                        eventKey: 'tab_items',
                                        component: <BusinessPartner_tab_Items businessPartnerId={businessPartnerId} businessPartnerTypeId={formData.businessPartnerTypeId} />
                                    },
                                    {
                                        title: 'Business-partner groups',
                                        eventKey: 'tab_businesspartnergroups',
                                        component: <BusinessPartner_tab_BusinessPartnerGroups businessPartnerId={businessPartnerId} businessPartnerTypeId={formData.businessPartnerTypeId} />
                                    },

                                ]}
                            />
                        </Col>
                    </Row>
                </Container>
            )}
            {showChangeReferenceCodesPopup && <EditBusinessPartnerReferencesPopup businessPartner={formData} show={showChangeReferenceCodesPopup} onHide={() => setShowChangeReferenceCodesPopup(false)} />}
            {showExportPopup && <BusinessPartner_popup_Export businessPartner={formData} show={showExportPopup} onHide={() => setShowExportPopup(false)} />}
            {showSpinner && <SpinnerOverlay />}
        </div>
    );
}
export default BusinessPartnerDetailsView;